'use client';

import { useState, useEffect } from 'react';
import { trackTocToggleClicked, trackTocAnchorClicked } from 'components/analytics/eventHandlerTracking';
import { addIdsToHeadings, getHeadingDetails } from './util';
import TableOfContents, { TocItem } from 'components/shared/TableOfContents/TableOfContents';

export interface BlogTableOfContentsProps {
  title?: string;
  contentWrapper?: string;
  expandedByDefault?: boolean;
}

/**
 * Blog-specific Table of Contents that scans the page for h2 headings.
 * @param title - the title of the table of contents
 * @param contentWrapper - the id of a div that contains h2s
 * @param expandedByDefault - whether the TOC starts expanded
 */
export const BlogTableOfContents = ({
  title = 'Table of Contents',
  contentWrapper = 'blog-content-wrapper',
  expandedByDefault = false,
}: BlogTableOfContentsProps) => {
  const [items, setItems] = useState<TocItem[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    addIdsToHeadings(contentWrapper);
    const headingDetails = getHeadingDetails(contentWrapper);
    const filteredHeadingDetails = headingDetails.filter((item): item is TocItem => item.label !== null);
    // oxlint-disable-next-line react/set-state-in-effect -- populates items from DOM heading query on mount
    setItems(filteredHeadingDetails);
    setIsDisabled(filteredHeadingDetails.length === 0);
  }, [contentWrapper]);

  const handleToggle = (isOpen: boolean) => {
    trackTocToggleClicked({
      action: isOpen ? 'open' : 'close',
    });
  };

  const handleItemClick = (item: TocItem, e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    trackTocAnchorClicked({
      anchor_label: `${e.currentTarget.textContent}: #${item.anchor}`,
    });
  };

  const itemStyles = 'h-auto py-2';

  return (
    <section id="table-of-contents">
      <TableOfContents
        items={items}
        title={title}
        classNameItem={itemStyles}
        defaultExpanded={expandedByDefault}
        disabled={isDisabled}
        testId="blogTableOfContentsTitle"
        onToggle={handleToggle}
        onItemClick={handleItemClick}
      />
    </section>
  );
};

export default BlogTableOfContents;
